const DANGEROUS_PROTOCOLS = ['javascript:', 'data:', 'vbscript:', 'file:'];

function isKeySafe(key: string): boolean {
  const safeKeyPattern = /^[a-zA-Z0-9_-]+$/;
  return safeKeyPattern.test(key);
}

function isValueSafe(value: string): boolean {
  const safeValuePattern = /^[a-zA-Z0-9_\-%.=&]*$/;
  return safeValuePattern.test(value) && !containsDangerousProtocols(value);
}

function containsDangerousProtocols(value: string): boolean {
  const valueLower = value.toLowerCase();

  for (const protocol of DANGEROUS_PROTOCOLS) {
    if (valueLower.includes(protocol)) {
      return true;
    }
  }

  return false;
}

export const sanitizeSearchParams = (search: string | undefined): string => {
  // Return empty string for undefined or empty search
  if (!search || typeof search !== 'string') {
    return '';
  }
  // Only allow search params that start with ? and contain safe characters
  const safeSearchPattern = /^[?][a-zA-Z0-9=&_\-%.]*$/;
  if (!safeSearchPattern.test(search)) {
    return '';
  }

  // Parse the search params to further validate them
  const searchParams = new URLSearchParams(search.substring(1));
  const sanitizedParams = new URLSearchParams();

  // Check each parameter individually
  for (const [key, value] of searchParams.entries()) {
    // Validate key and value
    if (!isKeySafe(key)) {
      continue;
    }

    if (!isValueSafe(value)) {
      continue;
    }

    // Parameter passed all checks, add it to sanitized params
    sanitizedParams.set(key, value);
  }

  // Construct the sanitized search string
  const sanitizedSearch = sanitizedParams.toString();
  return sanitizedSearch ? `?${sanitizedSearch}` : '';
};

/**
 * Filter an (already-safe) search string down to a set of known-good param
 * keys. Everything not on the allowlist is dropped. Key matching is
 * case-insensitive so partner-supplied casing (`quotenum`, `QUOTENUM`) is
 * handled the same as the canonical form. Values are preserved verbatim.
 *
 * Intended for use at navigation boundaries where a step should only carry
 * forward its documented, durable params (e.g. `lang`, `refCode`) and
 * intentionally shed everything else — deep-link tokens, tracking noise,
 * user-typed junk. This is the "allowlist on write" side of the standard
 * "sanitize on read / allowlist on write" URL discipline.
 *
 * The input is expected to already be `sanitizeSearchParams`-safe (starts
 * with `?` or is empty). Non-conforming input returns `''`.
 */
export const filterAllowedSearchParams = (search: string | undefined, allowlist: readonly string[]): string => {
  if (!search || typeof search !== 'string') {
    return '';
  }
  const safeSearchPattern = /^[?][a-zA-Z0-9=&_\-%.]*$/;
  if (!safeSearchPattern.test(search)) {
    return '';
  }
  const lowerAllow = new Set(allowlist.map(k => k.toLowerCase()));
  const source = new URLSearchParams(search.substring(1));
  const filtered = new URLSearchParams();
  for (const [key, value] of source.entries()) {
    if (!lowerAllow.has(key.toLowerCase())) {
      continue;
    }
    if (!isKeySafe(key) || !isValueSafe(value)) {
      continue;
    }
    // Emit under the *canonical* allowlist key so the URL is stable
    // regardless of the incoming casing.
    const canonical = allowlist.find(k => k.toLowerCase() === key.toLowerCase()) ?? key;
    filtered.set(canonical, value);
  }
  const out = filtered.toString();
  return out ? `?${out}` : '';
};

/**
 * Declarative description of a URL query param that switches the app into a
 * specific entry mode ("agent hand-off", "partner weblink", ...). The whole
 * point of this shape is to make precedence between competing entry modes
 * data-driven instead of hand-wired conditionals in a component.
 *
 * Matching is case-insensitive: `key: 'quoteNum'` matches `?quotenum=`,
 * `?QuoteNum=`, `?QUOTENUM=`, etc. Higher `weight` wins. Ties are broken by
 * the order of the `triggers` array passed to `resolveUrlParamPrecedence`.
 */
export interface UrlParamPrecedenceRule {
  /** Canonical key. Emitted as-is by the caller; matched case-insensitively. */
  key: string;
  /** Higher wins when multiple configured triggers appear on the same URL. */
  weight: number;
}

export interface UrlParamPrecedenceResult {
  /**
   * The winning trigger, or `null` if none of the configured triggers appear
   * on the URL. `urlKey` preserves the incoming casing so the caller can
   * strip the actual entry when the trigger has been consumed.
   */
  active: { key: string; urlKey: string; value: string } | null;
  /**
   * Actual URL keys (with incoming casing) that belong to lower-weight
   * triggers present on the URL. These are the keys the caller should
   * remove during URL cleanup so downstream code only ever sees one active
   * entry mode. The winning trigger's own key is NOT included — its
   * lifecycle (keep vs. strip after consumption) is domain-specific.
   */
  silencedUrlKeys: string[];
}

/**
 * Resolve which configured URL trigger "wins" on the current URL and which
 * lower-precedence triggers should be silenced. Pure function — no I/O, no
 * router coupling — so it's trivial to unit-test and safe to call in a
 * render body (typically wrapped in useMemo for stable identity).
 *
 * Example (coverme):
 *
 *   const TRIGGERS = [
 *     { key: 'refCode',  weight: 20 }, // partner weblink: fresh flow
 *     { key: 'quoteNum', weight: 10 }, // agent hand-off: policy retrieval
 *   ];
 *   const { active, silencedUrlKeys } = resolveUrlParamPrecedence(searchParams, TRIGGERS);
 *   // On ?refCode=CM&quoteNum=QTE1 → active.key === 'refCode',
 *   //   silencedUrlKeys === ['quoteNum'] (the exact incoming casing).
 */
export const resolveUrlParamPrecedence = (
  params: URLSearchParams,
  triggers: readonly UrlParamPrecedenceRule[]
): UrlParamPrecedenceResult => {
  type Match = { key: string; urlKey: string; value: string; weight: number; order: number };
  const matches: Match[] = [];
  triggers.forEach((trigger, order) => {
    const target = trigger.key.toLowerCase();
    for (const [urlKey, value] of params.entries()) {
      if (urlKey.toLowerCase() === target) {
        matches.push({ key: trigger.key, urlKey, value, weight: trigger.weight, order });
        break; // one URL entry per configured trigger — first wins on duplicates
      }
    }
  });
  if (matches.length === 0) {
    return { active: null, silencedUrlKeys: [] };
  }
  // Highest weight wins; ties broken by array order (stable).
  matches.sort((a, b) => b.weight - a.weight || a.order - b.order);
  const [winner, ...losers] = matches;
  return {
    active: { key: winner.key, urlKey: winner.urlKey, value: winner.value },
    silencedUrlKeys: losers.map(m => m.urlKey)
  };
};
